import dayjs, { Dayjs } from "dayjs";

import { Ref, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";

import DateRangeIcon from "@mui/icons-material/DateRange";
import { Box, IconButton, InputBaseComponentProps } from "@mui/material";
import { DateRangePickerToolbar, DateRangePickerToolbarProps } from "@mui/x-date-pickers-pro";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";

import { SEARCH_DATE_RANGE_PICKER_OPEN } from "@/constants/eventName";
import useDatePickerStyle from "@/hooks/useDatePickerStyle";
import useEvent from "@/hooks/useEvent";
import { dispatchEvent } from "@/hooks/useEventEmitter";

type RangeDate = [Dayjs | null, Dayjs | null];

interface SearchRangeDateProps {
  defaultValue?: RangeDate | undefined;
  onChange: (date: RangeDate) => void;
  label?: string | undefined;
  maxRangeDays?: number;
  calendarRangeDays?: number;
  customToolbar?: React.ReactNode;
  minDate?: Dayjs;
  needSameDate?: boolean;
}

function CustomToolbar(
  props: DateRangePickerToolbarProps<Dayjs> & {
    customToolbar?: React.ReactNode;
    ownerState?: { wrapperVariant: string };
  }
) {
  const isMobile = props.ownerState?.wrapperVariant === "mobile";

  return (
    <Box className={props.className} sx={{ display: "flex !important", flexDirection: "column" }}>
      {props.customToolbar ? (
        <div
          className="py-[16px] px-[12px] break-words"
          style={{ maxWidth: isMobile ? "320px" : "625px", paddingBottom: isMobile ? 0 : "16px" }}
        >
          {props.customToolbar}
        </div>
      ) : null}
      <DateRangePickerToolbar {...props} />
    </Box>
  );
}

const SearchRangeDate = (
  {
    label,
    defaultValue,
    onChange,
    maxRangeDays = 365,
    calendarRangeDays = 365,
    customToolbar,
    minDate: propsMinDage,
    needSameDate = true,
  }: SearchRangeDateProps,
  ref?: Ref<{ handleChange: (v: RangeDate) => void }>
) => {
  const defaultMaxDate = useMemo(() => dayjs().add(calendarRangeDays, "day"), [calendarRangeDays]);
  const defaultMinDate = useMemo(() => propsMinDage || dayjs(), [propsMinDage]);
  const [isOpen, setOpen] = useState(false);
  const [value, setValue] = useState<RangeDate>([null, null]);
  const [maxDate, setMaxDate] = useState(defaultMaxDate);
  const [minDate, setMinDate] = useState(defaultMinDate);

  const handleChange = useEvent((values: RangeDate) => {
    let nextValues: RangeDate = [...values];

    if (values[0] && !values[1]) {
      nextValues = [values[0], values[0]];
    }

    setValue(nextValues);
    onChange(nextValues);
  });

  const handleOpen = useEvent(() => {
    setOpen(true);
    dispatchEvent(SEARCH_DATE_RANGE_PICKER_OPEN, {});
  });

  const inputProps = useMemo(() => {
    const data: InputBaseComponentProps = { readOnly: true };

    if (label) {
      data.placeholder = label;
    }

    return data;
  }, [label]);

  useEffect(() => {
    if (value && value[0]) {
      let maxDate = dayjs(value[0]).add(maxRangeDays - 1, "day");

      if (dayjs(maxDate).startOf("day").diff(defaultMaxDate.startOf("day"), "day") > 0) {
        maxDate = defaultMaxDate;
      }

      setMinDate(value[0]);
      setMaxDate(maxDate);
    } else if (value && value[1]) {
      let minDate = dayjs(value[1]).subtract(maxRangeDays - 1, "day");

      if (dayjs(minDate).startOf("day").diff(defaultMinDate.startOf("day"), "day") < 0) {
        minDate = defaultMinDate;
      }

      setMinDate(minDate);
      setMaxDate(value[1]);
    } else {
      setMinDate(defaultMinDate);
      setMaxDate(defaultMaxDate);
    }
  }, [value, maxRangeDays, defaultMaxDate, defaultMinDate]);

  useEffect(() => {
    if (isOpen) {
      setValue([null, null]);
      onChange([null, null]);
    }
  }, [isOpen, onChange]);

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  const { paperStyle } = useDatePickerStyle(value);

  useImperativeHandle(
    ref,
    () => {
      return {
        handleChange,
      };
    },
    [handleChange]
  );

  return (
    <DateRangePicker
      minDate={minDate}
      maxDate={maxDate}
      value={value}
      defaultValue={defaultValue || undefined}
      onChange={handleChange}
      onOpen={handleOpen}
      onClose={() => {
        setOpen(false);
      }}
      format="ddd MM/DD/YY"
      slots={{
        field: SingleInputDateRangeField,
        toolbar: (props) => <CustomToolbar {...props} customToolbar={customToolbar} />,
      }}
      slotProps={{
        textField: {
          fullWidth: true,
          inputProps,
          InputProps: {
            style: { paddingLeft: "4px" },
            startAdornment: (
              <IconButton size="small">
                <DateRangeIcon />
              </IconButton>
            ),
          },
        },
        actionBar: () => ({
          actions: ["today", "clear", "cancel", "accept"],
        }),
        mobilePaper: paperStyle,
        desktopPaper: paperStyle,
        dialog: {
          classes: {
            root: "!z-[1000000]",
          },
        },
      }}
    />
  );
};

export default forwardRef(SearchRangeDate);
